import React, { useContext, useEffect, useState } from 'react'

import { capitalizeFirst } from '../components/Constants'
import { Option } from '../components/forms/FormProps'
import { useAuth } from './AuthContext'

import { EnumBase, EnumLocation } from '../response_types/backend/Enums'

export type EnumOptions = {
  gender_id: Option[]
  sample_type_id: Option[]
  healthcare_facility_id: Option[]
  patient_status_id: Option[]
  collection_location_id: Option[]
  collection_location_district: Option[]
  expected_species_id: Option[]
  host_scientific_name_id: Option[]
  dataset_id: Option[]
  research_study_id: Option[]
  study_branch_id: Option[]
}

// keyed by table (see `allEnums`), not by enum name - this is what the admin page renders
export type EnumData = {
  gender: EnumBase[]
  sample_type: EnumBase[]
  healthcare_facility: EnumBase[]
  patient_status: EnumBase[]
  location: EnumLocation[]
  expected_bacterial_species: EnumBase[]
  host_scientific_name: EnumBase[]
  oncology_dataset: EnumBase[]
  oncology_research_study: EnumBase[]
  oncology_study_branch: EnumBase[]
}

type EnumContextData = {
  enumOptions: EnumOptions
  enumData: EnumData
}

type EnumContextType = EnumContextData & {
  enumLoading: boolean
  reloadEnums: () => void
}

type EnumProviderProps = {
  children: React.ReactNode
}

type EnumItem = {
  name: string
  endpoint: string
  label: string
  convertToOptions: (data?: unknown) => Option[]
  table: string
}

// convert basic enum to Option[]
const convertToOptionsId: (data?: unknown) => Option[] = (data) =>
  data ? (data as EnumBase[]).map((item) => ({ label: capitalizeFirst(item.value), id: item.id, value: item.id })) : []

const allEnums: EnumItem[] = [
  { table: 'gender', name: 'gender_id', endpoint: '/enums/gender', label: 'Sex', convertToOptions: convertToOptionsId },
  { table: 'sample_type', name: 'sample_type_id', endpoint: '/enums/sample_type', label: 'Sample type (material)', convertToOptions: convertToOptionsId },
  {
    table: 'healthcare_facility',
    name: 'healthcare_facility_id',
    endpoint: '/enums/healthcare_facility',
    label: 'Healthcare facility',
    convertToOptions: convertToOptionsId,
  },
  { table: 'patient_status', name: 'patient_status_id', endpoint: '/enums/patient_status', label: 'Patient status', convertToOptions: convertToOptionsId },
  {
    name: 'collection_location_id',
    table: 'location',
    endpoint: '/enums/location',
    label: 'Sample collection location',
    convertToOptions: (data) =>
      data
        ? (data as EnumLocation[]).map((item) => ({
            label: `${capitalizeFirst(item.district)} - ${capitalizeFirst(item.town)} (${capitalizeFirst(item.region)})`,
            id: item.id,
            value: item.id,
          }))
        : [],
  },
  {
    name: 'collection_location_district',
    table: 'location',
    endpoint: '/enums/location',
    label: 'Sample collection location only district',
    convertToOptions: (data) =>
      data
        ? (data as EnumLocation[]).map((item) => ({
            label: item.district,
            id: item.id,
            value: item.id,
          }))
        : [],
  },
  {
    table: 'expected_bacterial_species',
    name: 'expected_species_id',
    endpoint: '/enums/expected_bacterial_species',
    label: 'Expected bacterial species',
    convertToOptions: convertToOptionsId,
  },
  {
    table: 'host_scientific_name',
    name: 'host_scientific_name_id',
    endpoint: '/enums/host_scientific_name',
    label: 'Host scientific name',
    convertToOptions: convertToOptionsId,
  },
  { table: 'oncology_dataset', name: 'dataset_id', endpoint: '/enums/oncology_dataset', label: 'Dataset', convertToOptions: convertToOptionsId },
  {
    table: 'oncology_research_study',
    name: 'research_study_id',
    endpoint: '/enums/oncology_research_study',
    label: 'Research study',
    convertToOptions: convertToOptionsId,
  },
  {
    table: 'oncology_study_branch',
    name: 'study_branch_id',
    endpoint: '/enums/oncology_study_branch',
    label: 'Study branch',
    convertToOptions: convertToOptionsId,
  },
]

// A single table can be exposed under several enum names (e.g. `collection_location_id` and
// `collection_location_district` are both the `location` table), so dedupe to keep one entry per table.
export const ENUMS = Array.from(new Set(allEnums.map((e) => e.table)))
export type EnumType = (typeof ENUMS)[number]

export const allEnumsMap: Record<string, EnumItem> = Object.fromEntries(allEnums.map((item) => [item.name, item]))

const EnumContext = React.createContext<EnumContextType>({ enumOptions: {} as EnumOptions, enumData: {} as EnumData, enumLoading: true, reloadEnums: () => {} })

export const useEnums = (): EnumContextType => {
  return useContext(EnumContext)
}

export const EnumProvider: React.FC<EnumProviderProps> = ({ children }) => {
  const { getParallelRequests, token } = useAuth()

  const [enumContextData, setEnumContextData] = useState<EnumContextData | undefined>(undefined)
  const [enumLoading, setEnumLoading] = useState(true)
  const [reload, setReload] = useState<number>(0)

  useEffect(() => {
    // wait for token to be ready if it's supposed to be there
    // if AuthProvider is still loading, this provider won't even be rendered
    // but we check token just in case
    if (!token) {
      setEnumLoading(false)
      return
    }

    let isMounted = true
    const fetchAllEnums = async () => {
      setEnumLoading(true)
      try {
        // fetch all data from backend using provided function
        const responses = await getParallelRequests(allEnums.map((item) => ({ endpoint: item.endpoint })))

        // check if all fetched correctly
        const allOk = responses.every((response) => response.status === 200 && response.json !== undefined)
        if (allOk && isMounted) {
          const jsons = responses.map((r) => (Array.isArray(r.json) ? r.json : []))
          // convert data from backend with convert functions
          const enumData = Object.fromEntries(allEnums.map((item, id) => [item.table, jsons[id]])) as EnumData
          const enumOptions = Object.fromEntries(allEnums.map((item, id) => [item.name, item.convertToOptions(jsons[id])])) as EnumOptions

          setEnumContextData({ enumData, enumOptions })
          setEnumLoading(false)
        }
      } catch (error) {
        console.error('Failed to fetch enums:', error)
        // we might want to keep loading=true or show an error state
        // for now we set loading false to avoid infinite spinner, but without data
        if (isMounted) setEnumLoading(false)
      }
    }

    fetchAllEnums()
    return () => {
      isMounted = false
    }
  }, [reload, token, getParallelRequests])

  const value = React.useMemo(
    () => ({
      enumOptions: enumContextData?.enumOptions ?? ({} as EnumOptions),
      enumData: enumContextData?.enumData ?? ({} as EnumData),
      enumLoading,
      reloadEnums: () => {
        setReload((reload) => reload + 1)
      },
    }),
    [enumContextData, enumLoading]
  )

  return <EnumContext.Provider value={value}>{children}</EnumContext.Provider>
}
